import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link
} from '@mui/material';
import { styled } from '@mui/material/styles';
import categoryService from '../services/categoryService';
import CategoryIcon from '@mui/icons-material/Category';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const StyledCard = styled(Card)(({ theme, selected }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
    borderColor: theme.palette.primary.main,
  },
}));

const SubcategorySelector = ({ 
  open, 
  onClose, 
  onSelect, 
  selectedCategory = null, 
  selectedSubcategory = null 
}) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(selectedCategory ? 'subcategory' : 'category');
  const [currentCategory, setCurrentCategory] = useState(selectedCategory);

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryService.getCategoriesForSelect();
      setCategories(data);
      setError(null);
    } catch (err) {
      setError('Failed to load categories. Please try again.');
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (category) => {
    setCurrentCategory(category);
    setStep('subcategory');
  };

  const handleSubcategorySelect = (subcategory) => {
    onSelect(currentCategory, subcategory);
    onClose();
  };

  const handleBack = () => {
    if (step === 'subcategory') {
      setStep('category');
      setCurrentCategory(null);
    }
  };

  const handleClose = () => {
    setStep(selectedCategory ? 'subcategory' : 'category');
    setCurrentCategory(selectedCategory);
    onClose();
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {step === 'category' ? 'Select Category' : 'Select Subcategory'}
          </Typography>
          {step === 'subcategory' && (
            <Breadcrumbs>
              <Link
                component="button"
                variant="body2"
                onClick={handleBack}
                sx={{ textDecoration: 'none' }}
              >
                Categories
              </Link>
              <Typography variant="body2" color="text.primary">
                {currentCategory?.name}
              </Typography>
            </Breadcrumbs>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 'category' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose a category for your offer. You'll then select a specific subcategory.
            </Typography>
            
            <Grid container spacing={2}>
              {categories.map((category) => (
                <Grid item xs={12} sm={6} md={4} key={category._id}>
                  <StyledCard
                    onClick={() => handleCategorySelect(category)}
                    selected={currentCategory?._id === category._id}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <CategoryIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        {category.name}
                      </Typography>
                      <Chip 
                        label={`${category.subcategories.length} subcategories`}
                        size="small"
                        variant="outlined"
                      />
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {step === 'subcategory' && currentCategory && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select a subcategory within <strong>{currentCategory.name}</strong> for your offer.
            </Typography>
            
            <Grid container spacing={2}>
              {currentCategory.subcategories.map((subcategory) => (
                <Grid item xs={12} sm={6} md={4} key={subcategory._id}>
                  <StyledCard
                    onClick={() => handleSubcategorySelect(subcategory)}
                    selected={selectedSubcategory?._id === subcategory._id}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <ArrowForwardIcon sx={{ color: 'primary.main' }} />
                      </Box>
                      <Typography variant="h6" gutterBottom>
                        {subcategory.name}
                      </Typography>
                      {selectedSubcategory?._id === subcategory._id && (
                        <CheckCircleIcon sx={{ color: 'success.main', mt: 1 }} />
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>

            {currentCategory.subcategories.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No subcategories available in this category.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'subcategory' && (
          <Button onClick={handleBack}>
            Back to Categories
          </Button>
        )}
        <Button 
          onClick={handleClose}
          variant="contained"
          data-cancel="true"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubcategorySelector; 